/**
 * FikoBrep Module - BREP Topology & Geometry Analysis
 *
 * Bu modül mesh geometrisinden BREP (Boundary Representation) topoloji
 * ve karakteristik özelliklerini çıkarmak için kullanılır.
 */

// Ana BREP yapıları ve tipleri
export { FikoBrep } from './FikoBrep';
export type {
    BrepVertex,
    BrepEdge,
    BrepFace,
    BrepLoop,
    BrepShell,
    BrepSolid,
    BrepBody,
    BrepCurve,
    BrepSurface,
    MeshAnalysisResult
} from './FikoBrep';

// WASM-accelerated analyzer
export { FikoBrepWasmAnalyzer } from './FikoBrepWasmAnalyzer';

// GPU-accelerated grup bazlı extractor
export { FikoGPUBrepExtractor } from './FikoGPUBrepExtractor';
export type {
    GrupBrepResult,
    SurfaceInfo,
    HoleInfo,
    PocketInfo,
    BossInfo,
    FilletInfo,
    ChamferInfo,
    RibInfo,
    // Manufacturing Analysis Types
    WallThicknessInfo,
    SymmetryInfo,
    DraftAngleInfo,
    UndercutInfo,
    PatternInfo,
    ThreadInfo,
    ManufacturingAnalysis,
    // Feature Detection Types
    SlotInfo,
    GrooveInfo,
    StepInfo,
    // Curvature Analysis
    CurvatureAnalysis,
    // Mass Properties
    MassPropertiesInfo,
    // Advanced Feature Detection Types
    DovetailInfo,
    FlangeInfo,
    KeywayInfo,
    CounterboreCountersinkInfo,
    KnurlInfo,
    PartingLineInfo
} from './FikoGPUBrepExtractor';

// BREP serialization
export { FikoBrepSerializer } from './FikoBrepSerializer';

// Surface recognition utilities
export { FikoBrepSurfaceRecognition } from './FikoBrepSurfaceRecognition';

// Curve Builder - Line, Spline, Polyline BRep entegrasyonu
export {
    FikoBrepCurveBuilder,
    getCurveBuilder,
    disposeCurveBuilder
} from './FikoBrepCurveBuilder';

// DFM (Design for Manufacturing) Analysis System
export {
    FikoDFMAnalyzer,
    getDFMAnalyzer,
    disposeDFMAnalyzer
} from './FikoDFMAnalyzer';
export type {
    DFMSeverity,
    ManufacturingProcess,
    DFMIssue,
    DFMScore,
    CostEstimate,
    DFMCostParams,
    DFMReportSection,
    DFMReport
} from './FikoDFMAnalyzer';

// ═══════════════════════════════════════════════════════════════════
// Manufacturing Cost Estimation System (Endüstriyel Maliyet Motoru)
// ═══════════════════════════════════════════════════════════════════

// Material Database - Malzeme veritabanı (fiyat, yoğunluk, özellikler)
export {
    FikoMaterialDB,
    getMaterialDB,
    disposeMaterialDB
} from './FikoMaterialDB';
export type {
    Currency,
    CurrencyRates,
    MaterialCategory,
    ManufacturingProcessType,
    MaterialProperties,
    MachineParameters,
    ToleranceCostMultiplier,
    SurfaceFinishCostMultiplier
} from './FikoMaterialDB';

// Cost Engine - Ana maliyet hesaplama motoru
export {
    FikoCostEngine,
    getCostEngine,
    disposeCostEngine
} from './FikoCostEngine';
export type {
    PartGeometry,
    DetailedCostBreakdown,
    CostLineItem,
    ProcessCostComparison,
    CostEstimationInput,
    CostEstimationResult
} from './FikoCostEngine';

// Casting Cost - Döküm maliyet modülü (kum, basınçlı, hassas, kabuk, santrifüj)
export { FikoCastingCost } from './FikoCastingCost';
export type {
    CastingProcessType,
    CastingCostInput,
    CastingCostResult
} from './FikoCastingCost';

// CNC Cost - CNC işleme maliyet modülü (3-eksen, 5-eksen, torna, EDM, taşlama)
export { FikoCNCCost } from './FikoCNCCost';
export type {
    CNCProcessType,
    CNCCostInput,
    CNCCostResult
} from './FikoCNCCost';

// Sheet Metal Cost - Sac metal maliyet modülü (lazer, plazma, waterjet, punç)
export { FikoSheetMetalCost } from './FikoSheetMetalCost';
export type {
    SheetMetalCuttingProcess,
    SheetMetalCostInput,
    SheetMetalCostResult
} from './FikoSheetMetalCost';

// Injection Molding Cost - Enjeksiyon kalıplama maliyet modülü
export { FikoInjectionMoldingCost } from './FikoInjectionMoldingCost';
export type {
    InjectionMoldingCostInput,
    InjectionMoldingCostResult
} from './FikoInjectionMoldingCost';

// 3D Print Cost - 3D baskı maliyet modülü (FDM, SLA, SLS, DMLS, MJF)
export { Fiko3DPrintCost } from './Fiko3DPrintCost';
export type {
    Print3DProcessType,
    Print3DCostInput,
    Print3DCostResult
} from './Fiko3DPrintCost';

// Forging, Welding, Heat/Surface Treatment Cost
// Dövme, kaynak, ısıl işlem, yüzey işlem maliyet modülü
export { FikoForgingWeldingCost } from './FikoForgingWeldingCost';
export type {
    ForgingProcessType,
    ForgingCostInput,
    ForgingCostResult,
    WeldingProcessType,
    WeldingCostInput,
    WeldingCostResult,
    HeatTreatmentType,
    HeatTreatmentCostInput,
    HeatTreatmentCostResult,
    SurfaceTreatmentType,
    SurfaceTreatmentCostInput,
    SurfaceTreatmentCostResult
} from './FikoForgingWeldingCost';

// ═══════════════════════════════════════════════════════════════════
// Material Recommendation Engine (Malzeme Oneri Motoru)
// ═══════════════════════════════════════════════════════════════════

export {
    FikoMaterialRecommender,
    getMaterialRecommender,
    disposeMaterialRecommender
} from './FikoMaterialRecommender';
export type {
    MaterialRequirements,
    MaterialRecommendation,
    RecommendationResult
} from './FikoMaterialRecommender';
